import { IUser } from "../api/User/User.types";

const AGL_DASHBOARD_APP_NAME = "agl-dashboard";
const AGL_DASHBOARD_APP_ID = 2;
const USER_APPS_STORAGE_KEY = "userApps";

const AUTH_API_BASE =
  process.env.REACT_APP_AUTH_BACKEND_SERVICE_URL || process.env.REACT_APP_AUTH_BACKEND_SERVICE_URL || "";
const USER_DETAILS_PATH = "/api/user_details";

/**
 * Decode JWT payload without verification (client-side only).
 * Handles standard 3-part JWT and base64url. Returns the payload object or null if invalid.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  if (!token || typeof token !== "string") return null;
  const t = token.trim();
  try {
    const parts = t.split(".");
    let payloadSegment: string;
    if (parts.length >= 2) {
      payloadSegment = parts[1];
    } else if (parts.length === 1) {
      payloadSegment = t;
    } else {
      return null;
    }
    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    let json: string;
    try {
      json = decodeURIComponent(
        decoded
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
    } catch {
      json = decoded;
    }
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Try to parse a single base64 string (no dots) as JSON payload.
 */
function decodeSinglePartPayload(token: string): Record<string, unknown> | null {
  if (!token?.trim()) return null;
  try {
    const base64 = token.trim().replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    let json: string;
    try {
      json = decodeURIComponent(
        decoded
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
    } catch {
      json = decoded;
    }
    const out = JSON.parse(json);
    return out && typeof out === "object" ? out : null;
  } catch {
    return null;
  }
}

interface UserAppEntry {
  app_name?: string;
  user_json?: string;
  app_id?: number;
  [key: string]: unknown;
}

/** Normalize parsed user_json (or raw user object) to IUser. */
function parseRawUserJsonToIUser(raw: Record<string, unknown>): IUser | null {
  if (!raw || typeof raw !== "object") return null;
  try {
    const user: IUser = {
      userId: String(raw.userID ?? raw.userId ?? ""),
      accountType: (raw.accountType as IUser["accountType"]) ?? 3,
      sessionID: String(raw.sessionID ?? ""),
      permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
      name: String(raw.name ?? ""),
      email: String(raw.email ?? ""),
      username: String(raw.username ?? ""),
    };
    if (raw.defaultConsignee != null)
      user.defaultConsignee = raw.defaultConsignee as IUser["defaultConsignee"];
    if (raw.shippers != null) user.shippers = raw.shippers as IUser["shippers"];
    if (raw.consignees != null)
      user.consignees = raw.consignees as IUser["consignees"];
    if (raw.portalNotice != null)
      user.portalNotice = String(raw.portalNotice);
    if (raw.image != null) user.image = String(raw.image);
    return user;
  } catch {
    return null;
  }
}

function extractUserFromPayload(inner: Record<string, unknown>): IUser | null {
  const userApps = inner.userApps as UserAppEntry[] | undefined;
  if (!Array.isArray(userApps)) return null;

  const app = userApps.find(
    (a) => (a?.app_name ?? "").toLowerCase() === AGL_DASHBOARD_APP_NAME
  );
  if (!app?.user_json || typeof app.user_json !== "string") return null;

  try {
    const raw = JSON.parse(app.user_json) as Record<string, unknown>;
    return parseRawUserJsonToIUser(raw);
  } catch {
    return null;
  }
}

/**
 * Override IUser fields from user_details API data (id, username, entity_url_email, first_name, last_name).
 */
function overrideUserFromApiData(
  user: IUser,
  data: Record<string, unknown>
): IUser {
  const next = { ...user };
  if (data.id != null) next.userId = String(data.id);
  if (data.username != null && data.username !== "") next.username = String(data.username);
  if (data.entity_url_email != null && data.entity_url_email !== "")
    next.email = String(data.entity_url_email);
  else if (data.username != null && data.username !== "") next.email = String(data.username);
  const firstName = data.first_name != null ? String(data.first_name) : "";
  const lastName = data.last_name != null ? String(data.last_name) : "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (fullName) next.name = fullName;
  else if (data.username != null && data.username !== "") next.name = String(data.username);
  return next;
}

/**
 * Extract IUser from user_details API response body.
 * Expected shape: { status, message, data: { id, username, entity_url_email, first_name, last_name, userApps: [{ app_id, user_json, ... }] } }.
 * Overrides userId, username, email, name from data when present.
 */
function extractUserFromUserDetailsResponse(body: unknown): IUser | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  const data = obj.data as Record<string, unknown> | undefined;
  const target = data && typeof data === "object" ? data : obj;
  const userApps = target.userApps as UserAppEntry[] | undefined;
  let user: IUser | null = null;
  if (Array.isArray(userApps)) {
    const entry = userApps.find(
      (a) => Number(a?.app_id) === AGL_DASHBOARD_APP_ID
    );
    if (!entry?.user_json || typeof entry.user_json !== "string") return null;
    try {
      const raw = JSON.parse(entry.user_json) as Record<string, unknown>;
      user = parseRawUserJsonToIUser(raw);
    } catch {
      return null;
    }
  } else if (typeof target.user_json === "string") {
    try {
      const raw = JSON.parse(target.user_json) as Record<string, unknown>;
      user = parseRawUserJsonToIUser(raw);
    } catch {
      return null;
    }
  } else {
    user = parseRawUserJsonToIUser(target);
  }
  if (!user) return null;
  if (data && typeof data === "object") {
    return overrideUserFromApiData(user, data);
  }
  return user;
}

/**
 * Fetch user details from the auth API using the access token.
 * Uses Authorization header with the raw token (no "Bearer " prefix).
 * Returns IUser or null on failure.
 */
export async function fetchUserDetails(accessToken: string): Promise<IUser | null> {
  if (!accessToken?.trim() || !AUTH_API_BASE) return null;
  try {
    const url = `${AUTH_API_BASE.replace(/\/$/, "")}${USER_DETAILS_PATH}`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: accessToken.trim(),
      },
      credentials: "omit",
    });
    if (!res.ok) return null;
    const body = await res.json();
    return extractUserFromUserDetailsResponse(body);
  } catch {
    return null;
  }
}

/**
 * Read userApps from localStorage, find entry with app_id === 2, parse user_json and return IUser.
 * Use this when the auth flow stores userApps in localStorage (e.g. after login).
 */
export function getUserFromUserAppsLocalStorage(): IUser | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const stored = localStorage.getItem(USER_APPS_STORAGE_KEY);
    if (!stored) return null;
    const userApps = JSON.parse(stored) as UserAppEntry[];
    if (!Array.isArray(userApps)) return null;
    const entry = userApps.find(
      (a) => Number(a?.app_id) === AGL_DASHBOARD_APP_ID
    );
    if (!entry?.user_json || typeof entry.user_json !== "string") return null;
    const raw = JSON.parse(entry.user_json) as Record<string, unknown>;
    return parseRawUserJsonToIUser(raw);
  } catch {
    return null;
  }
}

/**
 * Extract IUser from access_token. Supports:
 * 1. Nested JWT: outer payload.message = inner JWT string, inner has userApps.
 * 2. Token is the inner JWT directly (payload has userApps, no message wrapper).
 * 3. Single-part token: whole token is base64 JSON with "message" (inner JWT) or userApps.
 */
export function getUserFromAccessToken(accessToken: string): IUser | null {
  if (!accessToken?.trim()) return null;

  const outer = decodeJwtPayload(accessToken);
  if (!outer) {
    const single = decodeSinglePartPayload(accessToken);
    if (!single) return null;
    if (Array.isArray(single.userApps))
      return extractUserFromPayload(single as Record<string, unknown>);
    const msg = single.message;
    if (typeof msg === "string") {
      const inner = decodeJwtPayload(msg) ?? decodeSinglePartPayload(msg);
      if (inner) return extractUserFromPayload(inner);
    }
    return null;
  }

  if (Array.isArray(outer.userApps))
    return extractUserFromPayload(outer);

  const message = outer.message;
  const innerToken = typeof message === "string" ? message : null;
  if (!innerToken) return null;

  const inner = decodeJwtPayload(innerToken) ?? decodeSinglePartPayload(innerToken);
  if (!inner) return null;

  return extractUserFromPayload(inner);
}
